import { EPlankKind, IBalusterGeometry, IRailingGeometry, IStairCalculationResult, IStairConfig, IVec3 } from '@stair-craft/common/util';

import { EColorRef, ELineCap, ELineJoin, EPrimitiveKind, IPlanPrimitive, IPoint, IPolyPrimitive } from './plan-primitive';
import { IPlanOffset, stairOffset } from './top-plan-projection';

/** Line widths of the railing on every elevation, pixels — the ones the straight profile drew. */
const POST_WIDTH_PX: number = 3;
const BALUSTER_WIDTH_PX: number = 1.5;
const HANDRAIL_WIDTH_PX: number = 2.5;
const PLANK_WIDTH_PX: number = 2;

/** A plan point in stair millimetres → the elevation's horizontal millimetre. */
export type IElevationAcross = (point: { x: number; z: number }) => number;

/** The elevation's mapping of its own millimetres to pane pixels. */
export interface IElevationMap {
    across: IElevationAcross;
    wx: (mm: number) => number;
    wy: (mm: number) => number;
    /** Leave out the upper-floor railing — for a view that does not draw the upper floor. */
    stairOnly?: boolean;
}

function stroke(points: IPoint[], color: EColorRef, width: number): IPolyPrimitive {
    return { points, width, kind: EPrimitiveKind.Poly, closed: false, stroke: color, cap: ELineCap.Round, join: ELineJoin.Round };
}

/** A baluster or a post: an upright from its foot to its top. */
function upright(
    item: IBalusterGeometry,
    toStair: (point: IVec3) => IVec3,
    map: IElevationMap,
    color: EColorRef,
    width: number
): IPolyPrimitive {
    const at: number = map.wx(map.across(toStair(item.position)));
    return stroke(
        [
            [at, map.wy(item.position.y)],
            [at, map.wy(item.position.y + item.height)],
        ],
        color,
        width
    );
}

function pushRailing(primitives: IPlanPrimitive[], railing: IRailingGeometry, toStair: (point: IVec3) => IVec3, map: IElevationMap): void {
    for (const baluster of railing.balusters) {
        primitives.push(upright(baluster, toStair, map, EColorRef.Baluster, BALUSTER_WIDTH_PX));
    }
    for (const post of railing.posts) {
        primitives.push(upright(post, toStair, map, EColorRef.Post, POST_WIDTH_PX));
    }
    const path: IPoint[] = railing.handrail.points.map((point: IVec3): IPoint => {
        const stair: IVec3 = toStair(point);
        return [map.wx(map.across(stair)), map.wy(stair.y)];
    });
    if (path.length >= 2) {
        primitives.push(stroke(path, EColorRef.Handrail, HANDRAIL_WIDTH_PX));
    }
    // Planks (SC-CALC-092): the top one in the handrail's colour, the bottom one in the posts'.
    for (const plank of railing.planks ?? []) {
        const ends: IPoint[] = plank.points.map((point: IVec3): IPoint => {
            const stair: IVec3 = toStair(point);
            return [map.wx(map.across(stair)), map.wy(stair.y)];
        });
        primitives.push(stroke(ends, plank.kind === EPlankKind.Top ? EColorRef.Handrail : EColorRef.Post, PLANK_WIDTH_PX));
    }
}

/**
 * The railing of an elevation, from the calculation's own railing geometry — the same for the
 * profile, the front view and the side view of every type, on the sheet and on «Фасады»:
 * balusters and posts as uprights from their foot to their top, the handrail along its path,
 * the planks along theirs.
 * The upper-floor railing is anchored in room millimetres and is brought to the stair's.
 */
export function pushElevationRailings(
    primitives: IPlanPrimitive[],
    result: IStairCalculationResult,
    config: IStairConfig,
    map: IElevationMap
): void {
    if (!config.railing.enabled) {
        return;
    }
    const offset: IPlanOffset = stairOffset(result, config);
    for (const railing of result.geometry.railings) {
        if (map.stairOnly && railing.kind === 'upper') {
            continue;
        }
        const toStair: (point: IVec3) => IVec3 =
            railing.kind === 'upper'
                ? (point: IVec3): IVec3 => ({ x: point.x - offset.x, y: point.y, z: point.z - offset.z })
                : (point: IVec3): IVec3 => point;
        pushRailing(primitives, railing, toStair, map);
    }
}

/**
 * The turn's support posts (SC-CALC-093) as uprights in the posts' colour, from the floor to
 * their top — drawn with or without a railing: they carry the landing either way.
 */
export function pushElevationSupports(primitives: IPlanPrimitive[], result: IStairCalculationResult, map: IElevationMap): void {
    const stair: (point: IVec3) => IVec3 = (point: IVec3): IVec3 => point;
    for (const post of result.geometry.supports?.posts ?? []) {
        primitives.push(upright(post, stair, map, EColorRef.Post, POST_WIDTH_PX));
    }
}

/**
 * The height of the stair's own railing top over the floor, millimetres — the handrail and the
 * tops of posts and balusters; 0 without a railing. A view that draws the railing keeps it in
 * its frame by this.
 */
export function stairRailingTop(result: IStairCalculationResult, config: IStairConfig): number {
    if (!config.railing.enabled) {
        return 0;
    }
    let top: number = 0;
    for (const railing of result.geometry.railings.filter((item: IRailingGeometry) => item.kind !== 'upper')) {
        for (const item of [...railing.balusters, ...railing.posts]) {
            top = Math.max(top, item.position.y + item.height);
        }
        for (const point of railing.handrail.points) {
            top = Math.max(top, point.y);
        }
    }
    return top;
}
